import type { StorefrontBuyerSessionResponse } from '@stokmerter/contracts';
import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';

import {
  completeBuyerPasswordReset,
  loadBuyerSession,
  loadPublicStorefront,
  registerBuyer,
  startBuyerLogin,
  startBuyerPasswordReset,
  StorefrontApiError,
  verifyBuyerLogin,
  verifyBuyerRegistration,
} from './api';
import { getOrCreateBuyerDevice } from './buyer-device';
import { CatalogPage } from './catalog-page';

type PublicStorefront = Awaited<ReturnType<typeof loadPublicStorefront>>['storefront'];
type Buyer = NonNullable<StorefrontBuyerSessionResponse['buyer']>;
type Route = 'login' | 'register' | 'forgot' | 'reset' | 'privacy' | 'terms';
type Notice = { kind: 'info' | 'success' | 'error'; text: string } | null;

function currentRoute(pathname: string): Route {
  if (pathname === '/kayit') return 'register';
  if (pathname === '/parolami-unuttum') return 'forgot';
  if (pathname === '/parola-yenile') return 'reset';
  if (pathname === '/gizlilik') return 'privacy';
  if (pathname === '/kullanim-kosullari') return 'terms';
  return 'login';
}

function errorNotice(error: unknown): Notice {
  return {
    kind: 'error',
    text: error instanceof StorefrontApiError ? error.message : 'İşlem tamamlanamadı.',
  };
}

function values(event: FormEvent<HTMLFormElement>): FormData {
  event.preventDefault();
  return new FormData(event.currentTarget);
}

function string(form: FormData, name: string): string {
  return String(form.get(name) ?? '').trim();
}

function Field(props: {
  label: string;
  name: string;
  type?: 'text' | 'email' | 'password' | 'tel';
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
}) {
  const { label, ...input } = props;
  return <label className="field"><span>{label}</span><input {...input} type={props.type ?? 'text'} /></label>;
}

function FormNotice({ notice }: { notice: Notice }) {
  return notice ? <p className={`form-notice ${notice.kind}`} role="status">{notice.text}</p> : null;
}

function OtpForm(props: { busy: boolean; notice: Notice; onSubmit: (code: string) => void }) {
  return (
    <form className="form-stack" onSubmit={(event) => props.onSubmit(string(values(event), 'code'))}>
      <Field label="E-postaya gelen 6 haneli kod" name="code" autoComplete="one-time-code" required />
      <button className="primary-button" disabled={props.busy} type="submit">
        {props.busy ? 'Doğrulanıyor…' : 'Kodu doğrula'}
      </button>
      <FormNotice notice={props.notice} />
    </form>
  );
}

function LoginCard(props: {
  onAuthenticated: (buyer: Buyer) => void;
  registrationEnabled: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);

  async function submitLogin(event: FormEvent<HTMLFormElement>) {
    const form = values(event);
    setBusy(true);
    setNotice(null);
    try {
      const submittedUsername = string(form, 'username');
      const response = await startBuyerLogin({
        username: submittedUsername,
        password: String(form.get('password') ?? ''),
        device: await getOrCreateBuyerDevice(),
      });
      if (response.nextStep === 'email_otp') {
        setChallengeId(response.challengeId);
        setNotice({ kind: 'info', text: 'Doğrulama kodu e-posta adresinize gönderildi.' });
      } else if (response.nextStep === 'approval_pending') {
        setNotice({ kind: 'info', text: 'Kaydınız firma onayını bekliyor.' });
      } else setNotice({ kind: 'error', text: 'Bu hesabın mağaza erişimi açık değil.' });
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      setBusy(false);
    }
  }

  async function submitOtp(code: string) {
    if (!challengeId) return;
    setBusy(true);
    setNotice(null);
    try {
      const response = await verifyBuyerLogin({ challengeId, code });
      if (response.nextStep === 'authenticated') {
        const session = await loadBuyerSession();
        if (!session.authenticated || !session.buyer) throw new Error('BUYER_SESSION_MISSING');
        props.onAuthenticated(session.buyer);
      }
      else setNotice({ kind: 'info', text: 'Bu tarayıcı firma yetkilisinin cihaz onayını bekliyor.' });
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      setBusy(false);
    }
  }

  return <><div className="card-heading"><p className="eyebrow">Müşteri girişi</p><h1>Mağazaya giriş yapın</h1>
    <p>Ürünleri, renkleri ve size özel fiyatları yalnız onaylı müşteriler görebilir.</p></div>
    {challengeId ? <OtpForm busy={busy} notice={notice} onSubmit={(code) => void submitOtp(code)} /> :
      <form className="form-stack" onSubmit={(event) => void submitLogin(event)}>
        <Field label="Kullanıcı adı" name="username" autoComplete="username" required />
        <Field label="Parola" name="password" type="password" autoComplete="current-password" required />
        <a className="text-link align-end" href="/parolami-unuttum">Parolamı unuttum</a>
        <button className="primary-button" disabled={busy} type="submit">{busy ? 'Giriş kontrol ediliyor…' : 'Giriş yap'}</button>
        <FormNotice notice={notice} />
      </form>}
    <p className="switch-copy">{props.registrationEnabled ? <>Hesabınız yok mu? <a href="/kayit">Müşteri kaydı oluşturun</a></> : 'Yeni müşteri kaydı şu anda kapalı.'}</p></>;
}

function RegisterCard() {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [complete, setComplete] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const form = values(event);
    setBusy(true);
    setNotice(null);
    try {
      const email = string(form, 'email');
      const response = await registerBuyer({
        username: string(form, 'username'),
        email,
        password: String(form.get('password') ?? ''),
        applicationProfile: {
          version: 1,
          contactName: string(form, 'contactName'),
          companyName: string(form, 'companyName'),
          email,
          phone: string(form, 'phone'),
          taxNumber: string(form, 'taxNumber') || null,
          taxOffice: string(form, 'taxOffice') || null,
          billingAddress: string(form, 'billingAddress'),
          shippingAddress: string(form, 'shippingAddress'),
        },
        device: await getOrCreateBuyerDevice(),
        legalAcknowledged: true,
      });
      setChallengeId(response.challengeId);
      setNotice({ kind: 'info', text: 'E-postanıza gönderilen kodu girerek kaydı doğrulayın.' });
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      setBusy(false);
    }
  }

  async function verify(code: string) {
    if (!challengeId) return;
    setBusy(true);
    setNotice(null);
    try {
      await verifyBuyerRegistration({ challengeId, code });
      setComplete(true);
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      setBusy(false);
    }
  }

  if (complete) return <><div className="card-heading"><p className="eyebrow">Kayıt alındı</p><h1>Firma onayı bekleniyor</h1>
    <p>E-posta adresiniz doğrulandı. Firma yetkilisi kaydınızı onayladığında bu cihazla giriş yapabilirsiniz.</p></div>
    <a className="primary-link" href="/giris">Giriş ekranına dön</a></>;

  return <><div className="card-heading"><p className="eyebrow">Yeni müşteri</p><h1>Mağaza üyeliği oluşturun</h1>
    <p>E-posta doğrulamasından sonra mağaza yetkilisinin onayı beklenecektir.</p></div>
    {challengeId ? <OtpForm busy={busy} notice={notice} onSubmit={(code) => void verify(code)} /> :
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <div className="field-grid">
          <Field label="Yetkili adı" name="contactName" autoComplete="name" required />
          <Field label="Firma adı" name="companyName" autoComplete="organization" required />
          <Field label="E-posta" name="email" type="email" autoComplete="email" required />
          <Field label="Telefon" name="phone" type="tel" autoComplete="tel" required />
          <Field label="Kullanıcı adı" name="username" autoComplete="username" minLength={3} required />
          <Field label="Parola (en az 12 karakter)" name="password" type="password" autoComplete="new-password" minLength={12} required />
          <Field label="Vergi numarası" name="taxNumber" autoComplete="off" />
          <Field label="Vergi dairesi" name="taxOffice" autoComplete="off" />
        </div>
        <label className="field"><span>Fatura adresi</span><textarea name="billingAddress" autoComplete="billing street-address" required rows={3} /></label>
        <label className="field"><span>Teslimat adresi</span><textarea name="shippingAddress" autoComplete="shipping street-address" required rows={3} /></label>
        <label className="consent-row"><input name="legal" type="checkbox" required /><span>Gizlilik metnini ve mağaza kullanım koşullarını okudum.</span></label>
        <button className="primary-button" disabled={busy} type="submit">{busy ? 'Gönderiliyor…' : 'Kaydı gönder'}</button>
        <FormNotice notice={notice} />
      </form>}
    <p className="switch-copy">Zaten hesabınız var mı? <a href="/giris">Giriş yapın</a></p></>;
}

function ForgotCard() {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    const form = values(event);
    setBusy(true);
    setNotice(null);
    try {
      const result = await startBuyerPasswordReset({ email: string(form, 'email') });
      setNotice({ kind: 'success', text: result.message });
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      setBusy(false);
    }
  }
  return <><div className="card-heading"><p className="eyebrow">Hesap kurtarma</p><h1>Parolanızı yenileyin</h1>
    <p>Kayıtlı olsun veya olmasın, girdiğiniz e-posta için aynı güvenli yanıt gösterilir.</p></div>
    <form className="form-stack" onSubmit={(event) => void submit(event)}><Field label="Doğrulanmış e-posta" name="email" type="email" autoComplete="email" required />
      <button className="primary-button" disabled={busy} type="submit">{busy ? 'Gönderiliyor…' : 'Sıfırlama bağlantısı iste'}</button><FormNotice notice={notice} /></form>
    <p className="switch-copy"><a href="/giris">Giriş ekranına dönün</a></p></>;
}

function ResetCard() {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [done, setDone] = useState(false);
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
  async function submit(event: FormEvent<HTMLFormElement>) {
    const form = values(event);
    const password = String(form.get('password') ?? '');
    if (password !== String(form.get('confirmation') ?? '')) {
      setNotice({ kind: 'error', text: 'Parolalar eşleşmiyor.' });
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await completeBuyerPasswordReset({ token, newPassword: password });
      setDone(true);
      window.history.replaceState(null, '', '/parola-yenile');
    } catch (error) {
      setNotice(errorNotice(error));
    } finally {
      setBusy(false);
    }
  }
  if (done) return <><div className="card-heading"><p className="eyebrow">Parola yenilendi</p><h1>Tüm oturumlar kapatıldı</h1><p>Yeni parolanızla yeniden giriş yapabilirsiniz.</p></div><a className="primary-link" href="/giris">Giriş yap</a></>;
  return <><div className="card-heading"><p className="eyebrow">Yeni parola</p><h1>Parolanızı belirleyin</h1><p>En az 12 karakterlik yeni bir parola kullanın.</p></div>
    {!token ? <FormNotice notice={{ kind: 'error', text: 'Parola yenileme bağlantısı eksik veya geçersiz.' }} /> :
      <form className="form-stack" onSubmit={(event) => void submit(event)}><Field label="Yeni parola" name="password" type="password" autoComplete="new-password" minLength={12} required /><Field label="Yeni parola tekrar" name="confirmation" type="password" autoComplete="new-password" minLength={12} required />
        <button className="primary-button" disabled={busy} type="submit">{busy ? 'Yenileniyor…' : 'Parolayı yenile'}</button><FormNotice notice={notice} /></form>}</>;
}

function LegalCard({ route }: { route: 'privacy' | 'terms' }) {
  return <><div className="card-heading"><p className="eyebrow">Bilgilendirme</p><h1>{route === 'privacy' ? 'Gizlilik ve KVKK' : 'Mağaza kullanım koşulları'}</h1><p>Nihai hukuki metin firma bilgileri ve veri saklama kararlarıyla yayımlanacaktır.</p></div><p className="switch-copy"><a href="/giris">Giriş ekranına dönün</a></p></>;
}

function StoreUnavailable() {
  return <main className="status-page"><div className="status-card"><h1>Mağaza kullanıma açık değil</h1><p>Adres hatalı olabilir veya firma mağazayı geçici olarak kapatmış olabilir.</p></div></main>;
}

export function StorefrontApp() {
  const [storefront, setStorefront] = useState<PublicStorefront | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [buyer, setBuyer] = useState<Buyer | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    void Promise.allSettled([
      loadPublicStorefront(controller.signal),
      loadBuyerSession(controller.signal),
    ]).then(([storefrontResult, sessionResult]) => {
      if (storefrontResult.status !== 'fulfilled') {
        setUnavailable(true);
        return;
      }
      setStorefront(storefrontResult.value.storefront);
      document.title = storefrontResult.value.storefront.branding.displayName;
      if (sessionResult.status === 'fulfilled' && sessionResult.value.authenticated) {
        setBuyer(sessionResult.value.buyer ?? null);
      }
    }).finally(() => setLoading(false));
    return () => controller.abort();
  }, []);
  if (loading) return <main className="status-page"><p className="loading-label">Mağaza hazırlanıyor…</p></main>;
  if (unavailable || !storefront) return <StoreUnavailable />;
  const route = currentRoute(window.location.pathname);
  if (route === 'login' && buyer) {
    return <CatalogPage
      accentColorToken={storefront.branding.accentColorToken}
      buyer={buyer}
      displayName={storefront.branding.displayName}
      onLoggedOut={() => setBuyer(null)}
      storefrontId={storefront.storefrontId}
    />;
  }
  return <main className="shell" data-accent={storefront.branding.accentColorToken}>
    <section className="brand-panel" aria-label="Mağaza bilgisi"><div className="brand-content"><h2>{storefront.branding.displayName}</h2></div></section>
    <section className="access-panel"><div className="access-wrap"><div className="access-card">
      {route === 'login' ? <LoginCard onAuthenticated={setBuyer} registrationEnabled={storefront.registrationEnabled} /> : null}
      {route === 'register' && storefront.registrationEnabled ? <RegisterCard /> : null}
      {route === 'register' && !storefront.registrationEnabled ? <LoginCard onAuthenticated={setBuyer} registrationEnabled={false} /> : null}
      {route === 'forgot' ? <ForgotCard /> : null}
      {route === 'reset' ? <ResetCard /> : null}
      {route === 'privacy' || route === 'terms' ? <LegalCard route={route} /> : null}
    </div><footer><a href="/gizlilik">Gizlilik</a><span aria-hidden="true">•</span><a href="/kullanim-kosullari">Kullanım koşulları</a></footer></div></section>
  </main>;
}
